import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchNotificationPreferences,
  updateNotificationPreferences,
} from '../lib/api';
import type {
  ApiError,
  NotificationCategory,
  NotificationPreference,
} from '../lib/api';
import { isSudoCancelled } from './authErrors';
import { useSudo } from './SudoProvider';
import { Skeleton } from './Skeleton';

const CATEGORY_LABEL: Record<NotificationCategory, string> = {
  billing: 'Billing',
  security: 'Security',
  room: 'Space activity',
  token: 'Tokens',
  digest: 'Daily digest',
};

const CATEGORY_DESCRIPTION: Record<NotificationCategory, string> = {
  billing: 'Payment, plan changes, invoice events.',
  security: 'Login from a new device, password changes, and admin access changes.',
  room: 'Member changes, message activity, archive events.',
  token: 'When an agent’s access is issued, revoked, or about to expire.',
  digest: 'End-of-day rollup of muted activity.',
};

const CHANNELS: { key: 'email_enabled' | 'banner_enabled' | 'webhook_enabled'; label: string }[] = [
  { key: 'email_enabled', label: 'Email' },
  { key: 'banner_enabled', label: 'In-app banner' },
  { key: 'webhook_enabled', label: 'Webhook' },
];

export function NotificationPrefsSection() {
  const queryClient = useQueryClient();
  const { runWithSudo } = useSudo();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: fetchNotificationPreferences,
  });

  const [draft, setDraft] = useState<NotificationPreference[]>([]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (data) {
      setDraft(data.preferences);
      setDirty(false);
    }
  }, [data]);

  const mutation = useMutation({
    // Route the save through the sudo gate: a 403 `sudo_required` opens the
    // SudoModal and, on confirm, this same call is retried transparently.
    mutationFn: (body: Parameters<typeof updateNotificationPreferences>[0]) =>
      runWithSudo(() => updateNotificationPreferences(body)),
    onSuccess: (resp) => {
      setDraft(resp.preferences);
      setDirty(false);
      queryClient.setQueryData(['notification-preferences'], resp);
    },
  });

  function toggle(
    cat: NotificationCategory,
    channel: 'email_enabled' | 'banner_enabled' | 'webhook_enabled',
    value: boolean,
  ) {
    setDraft((prev) =>
      prev.map((p) =>
        p.category === cat ? { ...p, [channel]: value } : p,
      ),
    );
    setDirty(true);
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!dirty) return;
    mutation.mutate({ preferences: draft });
  }

  if (isLoading) {
    return (
      <div className="settings-section">
        <h3>Notification preferences</h3>
        <Skeleton
          className="settings-section-loading"
          ariaLabel="Loading notification preferences"
          rows={5}
          variant="list"
        />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="settings-section settings-section-error" role="alert">
        Could not load notification preferences ({String(error)}).
      </div>
    );
  }

  // A cancelled sudo prompt is a benign abort (the user backed out), not a save
  // failure — don't surface it as an error banner.
  const apiErr = isSudoCancelled(mutation.error)
    ? null
    : (mutation.error as ApiError | null);

  return (
    <div className="settings-section">
      <h3>Notification preferences</h3>
      <p className="settings-hint">
        Per category, choose which channels deliver alerts. Webhook delivery is
        coming soon — those controls are read-only for now.
      </p>

      {mutation.isSuccess && !dirty && (
        <p className="settings-success" role="status" aria-live="polite">
          Preferences saved.
        </p>
      )}
      {apiErr && (
        <p className="settings-error" role="alert">
          {apiErr.message}
        </p>
      )}

      <form
        className="notif-prefs-form"
        onSubmit={handleSubmit}
        aria-label="Notification preferences"
      >
        <table className="notif-prefs-table">
          <thead>
            <tr>
              <th scope="col">Category</th>
              {CHANNELS.map((c) => (
                <th key={c.key} scope="col">
                  {c.label}
                  {/* Amendment 2 — grey = inactive: the Webhook column is read-only,
                      so it reads as "coming soon" rather than an enabled-looking control. */}
                  {c.key === 'webhook_enabled' && (
                    <span className="notif-prefs-soon"> · coming soon</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {draft.map((pref) => (
              <tr key={pref.category}>
                <th scope="row" className="notif-prefs-row-header">
                  <span className="notif-prefs-category-name">
                    {CATEGORY_LABEL[pref.category]}
                  </span>
                  <span className="notif-prefs-category-desc">
                    {CATEGORY_DESCRIPTION[pref.category]}
                  </span>
                </th>
                {CHANNELS.map((c) => {
                  const isWebhook = c.key === 'webhook_enabled';
                  return (
                    <td key={c.key}>
                      <label className="notif-prefs-toggle">
                        <input
                          type="checkbox"
                          checked={pref[c.key]}
                          disabled={isWebhook || mutation.isPending}
                          onChange={(e) => toggle(pref.category, c.key, e.target.checked)}
                          aria-label={`${CATEGORY_LABEL[pref.category]} — ${c.label}`}
                        />
                      </label>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>

        <div className="settings-actions">
          <button
            type="submit"
            className="settings-action-primary"
            disabled={!dirty || mutation.isPending}
          >
            {mutation.isPending && (
              <span className="button-spinner" aria-hidden="true" />
            )}
            {mutation.isPending ? 'Saving…' : 'Save preferences'}
          </button>
        </div>
      </form>

      <p className="settings-hint">
        Billing and Security alerts are always sent, whatever your digest
        settings. Webhook delivery is coming soon.
      </p>
    </div>
  );
}
